import React from "react";

export type ModalTone = "neutral" | "info" | "warn" | "risk" | "ok";
export type ModalSize = "sm" | "md" | "lg" | "xl";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Optional icon shown to the left of the title (already-sized, e.g. `<svg>` or string emoji). */
  icon?: React.ReactNode;
  /** Tints the title bar bottom border + icon background. */
  tone?: ModalTone;
  /** sm=420px, md=560px (default), lg=800px. Ignored if fullScreen. */
  size?: ModalSize;
  /** Stretches to viewport (mobile / large content). Default false. */
  fullScreen?: boolean;
  /** Footer buttons. Rendered right-aligned with 8px gap. */
  footer?: React.ReactNode;
  /** Optional className for body wrapper. */
  bodyClassName?: string;
  /** Disable click-outside close. Default false. */
  disableBackdropClose?: boolean;
  /** Disable Escape close. Default false. */
  disableEscClose?: boolean;
  /** Optional aria-label override (defaults to title). */
  ariaLabel?: string;
  /**
   * Optional content rendered between the header and the scrollable body
   * (e.g. a tab bar). Renders full-width with no extra padding; supply your
   * own padding / border-bottom inside this node.
   */
  subheader?: React.ReactNode;
  children: React.ReactNode;
}

const SIZE_PX: Record<ModalSize, number> = {
  sm: 420,
  md: 560,
  lg: 800,
  xl: 1040,
};

const TONE_SOFT: Record<
  Exclude<ModalTone, "neutral">,
  { bg: string; ink: string; border: string }
> = {
  info: {
    bg: "var(--info-soft)",
    ink: "var(--info-soft-ink)",
    border: "var(--info)",
  },
  warn: {
    bg: "var(--warn-soft)",
    ink: "var(--warn-soft-ink)",
    border: "var(--warn)",
  },
  risk: {
    bg: "var(--risk-soft)",
    ink: "var(--risk-soft-ink)",
    border: "var(--risk)",
  },
  ok: { bg: "var(--ok-soft)", ink: "var(--ok-soft-ink)", border: "var(--ok)" },
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  icon,
  tone = "neutral",
  size = "md",
  fullScreen = false,
  footer,
  bodyClassName,
  disableBackdropClose = false,
  disableEscClose = false,
  ariaLabel,
  subheader,
  children,
}: ModalProps): React.ReactElement | null {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  // Body scroll lock + focus capture/restore
  React.useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current =
      (typeof document !== "undefined"
        ? (document.activeElement as HTMLElement | null)
        : null) ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus panel on open
    const raf = window.requestAnimationFrame(() => {
      panelRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
  }, [open]);

  // Keyboard: Escape + focus trap
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (!disableEscClose) {
          e.stopPropagation();
          onClose();
        }
        return;
      }
      if (e.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = Array.from(
          panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusables.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !panel.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, disableEscClose, onClose]);

  if (!open) return null;

  const sizePx = SIZE_PX[size];
  const toneSoft = tone !== "neutral" ? TONE_SOFT[tone] : null;
  const computedAriaLabel =
    ariaLabel ?? (typeof title === "string" ? title : undefined);

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableBackdropClose) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const panelStyle: React.CSSProperties = fullScreen
    ? {
        width: "100vw",
        height: "100vh",
        maxHeight: "100vh",
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: 0,
        boxShadow: "var(--sh-pop)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }
    : {
        width: `min(100% - 32px, ${sizePx}px)`,
        maxHeight: "calc(100vh - 32px)",
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md)",
        boxShadow: "var(--sh-pop)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        outline: "none",
      };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 20px",
    borderBottom: `1px solid ${toneSoft ? toneSoft.border : "var(--line)"}`,
    background: toneSoft ? toneSoft.bg : "transparent",
  };

  const iconBoxStyle: React.CSSProperties = {
    width: 24,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    borderRadius: "var(--r-sm)",
    background: toneSoft ? toneSoft.bg : "var(--bg-sunken)",
    color: toneSoft ? toneSoft.ink : "var(--ink-2)",
    fontSize: 14,
    lineHeight: 1,
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    flex: 1,
    fontSize: 16,
    fontWeight: 600,
    color: "var(--ink-1)",
  };

  const closeBtnStyle: React.CSSProperties = {
    flex: "0 0 auto",
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: "var(--r-sm)",
    color: "var(--ink-2)",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    padding: 0,
  };

  const bodyStyle: React.CSSProperties = {
    padding: 20,
    overflowY: "auto",
    flex: 1,
    color: "var(--ink-1)",
  };

  const footerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    padding: "12px 20px",
    borderTop: "1px solid var(--line)",
    background: "var(--bg-sunken)",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={computedAriaLabel}
      onMouseDown={onBackdropMouseDown}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "var(--scrim)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: fullScreen ? 0 : 16,
      }}
    >
      <div ref={panelRef} tabIndex={-1} style={panelStyle}>
        <div style={headerStyle}>
          {icon !== undefined && (
            <span style={iconBoxStyle} aria-hidden>
              {icon}
            </span>
          )}
          <h2 style={titleStyle}>{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={closeBtnStyle}
          >
            ×
          </button>
        </div>
        {subheader !== undefined && subheader !== null && (
          <div style={{ flexShrink: 0 }}>{subheader}</div>
        )}
        <div className={bodyClassName} style={bodyStyle}>
          {children}
        </div>
        {footer !== undefined && footer !== null && (
          <div style={footerStyle}>{footer}</div>
        )}
      </div>
    </div>
  );
}

export default Modal;
